import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchOrder } from "../lib/api";
import { Check, Download, QrCode, Loader2 } from "lucide-react";

export default function OrderSuccessPage() {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => { fetchOrder(orderNumber).then(setOrder).catch(() => setOrder(null)); }, [orderNumber]);

  if (!order) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <Loader2 className="animate-spin text-nibro-sage-dark" />
      </div>
    );
  }

  const qrData = encodeURIComponent(`NIBRO-ORDER:${order.order_number}`);
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&bgcolor=ffffff&color=7A9C7F&qzone=1&data=${qrData}`;

  return (
    <div className="min-h-screen bg-white" data-testid="order-success-page">
      <div className="mx-auto max-w-[900px] px-5 py-16 lg:px-10">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
            <Check size={22} className="text-emerald-600" />
          </div>
          <div>
            <div className="label-uppercase">Bestelling bevestigd</div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900 sm:text-4xl">
              Bedankt voor uw bestelling, {order.customer_name.split(" ")[0]}!
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-zinc-600">
              U ontvangt uw factuur per e-mail op <strong>{order.customer_email}</strong> zodra uw order is gecontroleerd.
            </p>
          </div>
        </div>

        <div className="mt-10 rounded-md border-2 border-nibro-sage bg-white p-8">
          <div className="flex items-start justify-between gap-6">
            <div>
              <div className="label-uppercase">Ordernummer</div>
              <div className="font-mono text-xl font-bold text-nibro-sage-dark">{order.order_number}</div>
              <div className="mt-1 text-xs text-zinc-500">{new Date(order.created_at).toLocaleString("nl-NL")}</div>
            </div>
            <div className="flex flex-col items-center">
              <img src={qrUrl} alt="Order QR" className="h-32 w-32 rounded-md border border-nibro-sage/30" />
              <div className="mt-2 flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-nibro-sage-dark">
                <QrCode size={10} /> Tracking & herorder
              </div>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
            <Section title="Verzendadres">
              <div className="text-sm leading-relaxed text-zinc-800">
                {order.customer_name}<br />
                {order.company && <>{order.company}<br /></>}
                {order.shipping_address}<br />
                {order.shipping_postal} {order.shipping_city}<br />
                {order.shipping_country}
              </div>
            </Section>
            <Section title="Contact">
              <div className="text-sm leading-relaxed text-zinc-800">
                {order.customer_email}<br />
                {order.customer_phone}
              </div>
            </Section>
          </div>

          <div className="mt-8">
            <div className="label-uppercase mb-3">Items</div>
            <div className="space-y-2">
              {order.items.map((it, idx) => (
                <div key={idx} className="flex items-center justify-between rounded-md border border-zinc-200 bg-white px-4 py-3">
                  <div>
                    <div className="text-sm font-semibold text-zinc-900">{it.name}</div>
                    <div className="font-mono text-[11px] text-zinc-500">{it.sku} · {it.quantity} stk</div>
                  </div>
                  <div className="font-mono text-sm text-zinc-900">€{(it.quantity * it.unit_price).toFixed(2)}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-3 border-t border-zinc-100 pt-4 text-sm md:grid-cols-4">
            <Stat label="Subtotaal" value={`€${order.subtotal.toFixed(2)}`} />
            <Stat label="BTW" value={`€${order.vat.toFixed(2)}`} />
            <Stat label="Verzending" value={`€${order.shipping.toFixed(2)}`} />
            <Stat label="Totaal" value={`€${order.total.toFixed(2)}`} accent />
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.order_number}/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-md bg-nibro-sage px-5 py-3 text-sm font-bold text-white transition hover:bg-nibro-sage-hover"
            data-testid="download-pdf-button"
          >
            <Download size={14} /> Download order-PDF
          </a>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 rounded-md border-2 border-nibro-sage bg-white px-5 py-3 text-sm font-bold text-nibro-sage-dark transition hover:bg-nibro-sage-pale"
            data-testid="continue-shopping"
          >
            Verder winkelen
          </Link>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-md border border-zinc-200 bg-nibro-sage-pale p-4">
      <div className="label-uppercase mb-2">{title}</div>
      {children}
    </div>
  );
}
function Stat({ label, value, accent }) {
  return (
    <div>
      <div className="label-uppercase">{label}</div>
      <div className={`mt-0.5 font-display text-lg font-bold ${accent ? "text-nibro-sage-dark" : "text-zinc-900"}`}>{value}</div>
    </div>
  );
}
